import dayjs from "dayjs";
import { ServiceType } from "~/utils/constants";
import type { TServiceTicket } from "~/types";
import type { AddOnServiceDto } from "~/services/add-on-services/add-on-services.types";
import type { TStaffRole } from "~/types/staff";

// Calculate travel time in hours - only if vehicle is selected
export const getTravelTimeHours = (service: any): number => {
  // No vehicle = no travel time
  if (!service.vessel) {
    return 0;
  }

  // Check multiple possible sources for travel time
  // service.travelTime is from estimate form (already in hours or fractional)
  // location/address travelTime is typically in minutes
  if (service.travelTime !== undefined && service.travelTime !== null) {
    // Estimate form saves as hours (e.g., 0.5 = 30 min)
    return Number(service.travelTime);
  }

  const travelTimeMinutes =
    service.location?.travelTime ||
    service.client?.shippingAddresses?.[0]?.travelTime ||
    30; // Default 30 minutes
  return travelTimeMinutes / 60; // Convert to hours
};

// Get duration in hours
export const getDurationHours = (service: any): number => {
  if (service.timeIn && service.timeOut) {
    const timeIn = dayjs(service.timeIn);
    const timeOut = dayjs(service.timeOut);
    return timeOut.diff(timeIn, "hour", true);
  }
  return Number(service.duration || 1); // Default 1 hour
};

interface ServiceOverride {
  serviceId: string;
  customVariables?: {
    staffCount?: number;
    staffRate?: number;
    vesselRate?: number;
    hours?: number;
    cubicFeet?: number;
  };
  discountPercentage?: number;
  customTotal?: number;
}

interface CalculateServiceCostParams {
  service: TServiceTicket | any;
  artHandlerRole?: TStaffRole | null;
  addOnServices?: AddOnServiceDto[];
  serviceOverrides?: Map<string, ServiceOverride>;
}

export const calculateServiceCost = ({
  service,
  artHandlerRole,
  addOnServices = [],
  serviceOverrides = new Map(),
}: CalculateServiceCostParams) => {
  const override = serviceOverrides.get(service.id);

  const travelHours = getTravelTimeHours(service);
  const durationHours = getDurationHours(service);
  const totalHours = override?.customVariables?.hours || (travelHours + durationHours);

  let serviceCost = 0;
  let staffCost = 0;
  let vesselCost = 0;
  let staffCount = override?.customVariables?.staffCount || service.staffs?.length || 1; // Default varies by service type
  const staffHourlyRate = override?.customVariables?.staffRate || artHandlerRole?.hourlyRate || 100; // Default $100/hr
  let vesselHourlyRate = override?.customVariables?.vesselRate || 0;

  // Different calculation logic based on service type
  if (
    service.serviceType === ServiceType.COLLECTION ||
    service.serviceType === ServiceType.DELIVERY
  ) {
    // Collection/Delivery: (Staff + Vehicle) × Total Hours (travel + site time)
    // Use override staff count if provided, otherwise use actual staff or default to 2
    if (!override?.customVariables?.staffCount) {
      staffCount = service.staffs?.length || 2; // Default to 2 staff for transport
    }
    staffCost = staffCount * staffHourlyRate * totalHours;

    // Vehicle cost (only if vessel is assigned)
    if (service.vessel) {
      vesselHourlyRate = override?.customVariables?.vesselRate || service.vessel.hourlyRate || 90;
      vesselCost = vesselHourlyRate * totalHours;
    }

    serviceCost = staffCost + vesselCost;

  } else if (service.serviceType === ServiceType.ON_SITE_SERVICE) {
    // On-Site Service: Staff Only × Site Time (no travel, no vehicle) with $300 minimum per staff
    // Use override staff count if provided, otherwise use actual staff or default to 1
    if (!override?.customVariables?.staffCount) {
      staffCount = service.staffs?.length || 1; // Default to 1 staff for on-site
    }
    const siteTimeOnly = override?.customVariables?.hours || durationHours; // No travel time for on-site
    staffCost = staffCount * staffHourlyRate * siteTimeOnly;

    // $300 minimum per staff member
    const minimumCharge = 300 * staffCount;
    serviceCost = Math.max(staffCost, minimumCharge);

  } else if (service.serviceType === ServiceType.WAREHOUSE_SERVICE) {
    // Warehouse: Volume-based pricing ($1/ft³ with $75 minimum) - no hourly rates
    const totalCubicFeet = override?.customVariables?.cubicFeet || service.inventories?.reduce((total: number, inventory: any) => {
      const volume = ((inventory.length || 1) * (inventory.width || 1) * (inventory.height || 1)) / 1728;
      return total + volume;
    }, 0) || 60; // Default 60 cubic feet

    const warehouseRate = 1; // $1 per cubic foot
    const minimumCharge = 75; // $75 minimum
    serviceCost = Math.max(totalCubicFeet * warehouseRate, minimumCharge);

  } else if (service.serviceType === ServiceType.FABRICATION_SERVICE) {
    // Fabrication: Custom rates - typically per square foot or hourly for specialized work
    // Use override staff count if provided, otherwise use actual staff or default to 1
    if (!override?.customVariables?.staffCount) {
      staffCount = service.staffs?.length || 1; // Default to 1 specialist
    }
    // For now, use hourly rate for fabrication (could be enhanced with sq ft rates)
    const fabricationHours = override?.customVariables?.hours || durationHours; // Just site time, no travel
    staffCost = staffCount * (staffHourlyRate * 1.5) * fabricationHours; // 1.5x rate for specialized work
    serviceCost = staffCost;

  } else {
    // Fallback for other service types
    staffCost = staffCount * staffHourlyRate * totalHours;
    serviceCost = staffCost;
  }

  // Calculate supplies cost
  const suppliesCost = service.supplyItems?.reduce((total: number, supply: any) => {
    return total + ((supply.quantity || 1) * (supply.unitCost || 0));
  }, 0) || 0;

  // Calculate add-on services cost based on service type
  let addOnCost = 0;

  if (service.addOnServices && service.addOnServices.length > 0) {
    // For Collection, Delivery, and Site Service: add-ons are included (no extra charge)
    if (
      service.serviceType === ServiceType.COLLECTION ||
      service.serviceType === ServiceType.DELIVERY ||
      service.serviceType === ServiceType.ON_SITE_SERVICE
    ) {
      addOnCost = 0; // General services add-ons are included
    }
    // For Warehouse and Fabrication: calculate using volume/inventory
    else if (
      service.serviceType === ServiceType.WAREHOUSE_SERVICE ||
      service.serviceType === ServiceType.FABRICATION_SERVICE
    ) {
      // Calculate total volume from inventory for volume-based pricing
      const totalCubicFeet = service.inventories?.reduce((total: number, inventory: any) => {
        const volume = ((inventory.length || 1) * (inventory.width || 1) * (inventory.height || 1)) / 1728;
        return total + volume;
      }, 0) || 60; // Default 60 cubic feet

      addOnCost = service.addOnServices.reduce((total: number, addon: any) => {
        const addOnService = addOnServices?.find((aos: AddOnServiceDto) => aos.id === addon.id);
        if (addOnService?.rate) {
          if (addOnService.unit === 'CU_FT') {
            // Volume-based pricing for warehouse/fabrication with minimum pricing
            const volumeRate = addOnService.rate * totalCubicFeet;
            const minimumRate = addOnService.minimumCharge || 0;
            return total + Math.max(volumeRate, minimumRate);
          } else if (addOnService.unit === 'HOURLY') {
            return total + (addOnService.rate * totalHours);
          } else if (addOnService.unit === 'EACH') {
            return total + addOnService.rate;
          }
        }
        return total;
      }, 0);
    }
  }

  // Calculate base total
  const baseTotal = serviceCost + suppliesCost + addOnCost;

  // Apply discount if override exists
  let finalTotal = baseTotal;
  if (override?.discountPercentage) {
    const discountAmount = baseTotal * (override.discountPercentage / 100);
    finalTotal = baseTotal - discountAmount;
  }

  // Use custom total if provided (and it's greater than 0)
  if (override?.customTotal !== undefined && override.customTotal > 0) {
    finalTotal = override.customTotal;
  }

  return {
    serviceCost,
    suppliesCost,
    addOnCost,
    baseTotal,
    discountPercentage: override?.discountPercentage || 0,
    discountAmount: override?.discountPercentage ? baseTotal * (override.discountPercentage / 100) : 0,
    total: finalTotal,
    isOverridden: !!(override && (override.customTotal !== undefined || override.discountPercentage !== undefined || override.customVariables !== undefined)),
    breakdown: {
      staffCount,
      staffHourlyRate,
      staffCost,
      vesselHourlyRate,
      vesselCost,
      totalHours,
      travelHours,
      durationHours,
    }
  };
};


